"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase";
import { getSafetySnapshot, reportMember, setUserBlocked, type ReportType } from "@/app/actions/safety";

const SafetyContext = createContext({ userId: null as string | null, blockedIds: [] as string[], refresh: async () => {} });

export function SafetyProvider({ children }: { children: React.ReactNode }) {
  const [snapshot, setSnapshot] = useState({ userId: null as string | null, blockedIds: [] as string[] });
  const requestVersion = useRef(0);
  const refresh = useCallback(async () => {
    const version = ++requestVersion.current;
    try {
      const result = await getSafetySnapshot();
      if (version === requestVersion.current && !result.unavailable) {
        setSnapshot({ userId: result.userId, blockedIds: result.blockedIds });
      }
    } catch {
      // Existing blocking remains visible during a temporary network failure.
    }
  }, []);
  useEffect(() => {
    let active = true;
    const versionCounter = requestVersion;
    const client = createClient();
    client.auth.getSession().then(({ data: { session } }) => { if (active && session) void refresh(); });
    // Defer out of the auth callback to avoid Supabase's session lock.
    const { data: { subscription } } = client.auth.onAuthStateChange((event, session) => {
      if (!session) { versionCounter.current++; setSnapshot({ userId: null, blockedIds: [] }); }
      else if (event === "SIGNED_IN") setTimeout(() => { if (active) void refresh(); }, 0);
    });
    return () => { active = false; versionCounter.current++; subscription.unsubscribe(); };
  }, [refresh]);
  return <SafetyContext.Provider value={{ ...snapshot, refresh }}>{children}</SafetyContext.Provider>;
}

export function BlockableContent({ authorId, children }: { authorId: string | null | undefined; children: React.ReactNode }) {
  const { blockedIds } = useContext(SafetyContext);
  if (authorId && blockedIds.includes(authorId)) return <p className="text-sm text-text-muted p-4">Content from a blocked member is hidden. <Link href="/account/blocked" className="text-forest underline">Manage blocked members</Link></p>;
  return children;
}

export function useMemberBlocked(userId: string) {
  return useContext(SafetyContext).blockedIds.includes(userId);
}

export function useBlockedMemberIds() {
  return useContext(SafetyContext).blockedIds;
}

export function UserSafety({ userId, contentType = "profile", contentId = null }: { userId: string; contentType?: ReportType; contentId?: string | null }) {
  const router = useRouter();
  const { userId: currentUserId, blockedIds, refresh } = useContext(SafetyContext);
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [pending, startTransition] = useTransition();
  if (!currentUserId || currentUserId === userId) return null;
  const blocked = blockedIds.includes(userId);
  return <div className="my-3 text-sm">
    <div className="flex flex-wrap gap-2">
      <button disabled={pending} className="min-h-11 px-3 border border-stone rounded-sm text-text-muted" onClick={() => setReporting(!reporting)}>Report {contentType === "profile" ? "member" : contentType}</button>
      <button disabled={pending} className="min-h-11 px-3 border border-stone rounded-sm text-text-muted" onClick={() => startTransition(async () => {
        const result = await setUserBlocked(userId, !blocked);
        if (result.error) setMessage(result.error); else { await refresh(); router.refresh(); setMessage(blocked ? "Member unblocked." : "Member blocked. Direct messages and quotes are stopped, and their content is hidden from you."); }
      })}>{blocked ? "Unblock member" : "Block member"}</button>
    </div>
    {reporting && <form className="mt-3 border border-stone rounded-sm p-4" onSubmit={event => { event.preventDefault(); startTransition(async () => {
      const result = await reportMember(userId, contentType, contentId, reason);
      setMessage(result.error ?? "Report saved for the Tradesfolk team to review.");
      if (!result.error) { setReporting(false); setReason(""); }
    }); }}>
      <label className="block font-medium">Why are you reporting this?<textarea required maxLength={2000} value={reason} onChange={event => setReason(event.target.value)} className="block w-full mt-2 border border-stone rounded-sm p-3" rows={3} /></label>
      <button disabled={pending} className="min-h-11 mt-3 px-4 bg-forest text-white rounded-sm">{pending ? "Sending…" : "Send report"}</button>
    </form>}
    {message && <p role="status" className="mt-2 text-text-body">{message}</p>}
  </div>;
}
